import type { ReactNode } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { RequestError } from "@/app/queries";
import { useCohortMap, type CohortMap } from "@/app/analyticsQueries";
import { useCohorts, useMentorLoad, type Cohort } from "@/app/peopleQueries";
import { useMe } from "@/app/session";
import { EmptyState, NoAccess, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { Attention, Bottlenecks, MentorLoadCard } from "./mentor/cohort/Aside";
import { Matrix } from "./mentor/cohort/Matrix";
import { ScopeTabs } from "./mentor/cohort/ScopeTabs";
import { Stats } from "./mentor/cohort/Stats";
import { SCOPE_ALL, emptyBody, scopeLabel, scopeSub, scopeTabs, stats } from "./mentor/cohort/model";

/** M4 «Когорта»: карта прохождения студенты × 29 шагов, статистика, узкие места,
 *  «требуют внимания», нагрузка менторов. Режим — часть адреса: /mentor/cohort/{all|nocohort|id}. */
export function CohortPage({ scope }: { scope: string }) {
  const me = useMe();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const map = useCohortMap(scope);
  const cohorts = useCohorts();
  const load = useMentorLoad(scope);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );

  const err = map.error;
  if (err instanceof RequestError && err.status === 403)
    return frame(<NoAccess where="карта когорты" body="Карту прохождения когорты видят менторы и администраторы школы. Свой маршрут и гейты вы найдёте на главной." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound primary={{ label: "ко всем студентам", href: "/mentor/cohort/all" }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="карта когорты"
        tone="warn"
        title="Нет связи с сервером"
        body="Карта не загрузилась. Гейты и сдачи хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: () => void map.refetch() }}
      />,
    );
  if (!map.data) return frame(<SkeletonRows />);
  return <CohortView scope={scope} map={map.data} cohorts={cohorts.data?.cohorts} noCohort={cohorts.data?.no_cohort} load={load} user={user} />;
}

function CohortView({
  scope,
  map,
  cohorts,
  noCohort,
  load,
  user,
}: {
  scope: string;
  map: CohortMap;
  cohorts?: Cohort[];
  noCohort?: number;
  load: ReturnType<typeof useMentorLoad>;
  user?: { name: string; role: string };
}) {
  const tabs = scopeTabs(cohorts, noCohort);
  return (
    <Shell nav wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-4 py-2.5 md:px-[22px]">
        <ScopeTabs tabs={tabs} value={scope} />
        <span className="flex-1" />
        <span className="font-mono text-[11.5px] text-mut" data-testid="scope-sub">
          {scopeSub(scope, map, cohorts)}
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_372px]">
        <div className="flex min-w-0 flex-col gap-[22px] px-4 pb-7 pt-[18px] md:px-[22px]">
          <h1 className="m-0 font-mono text-[20px] font-bold tracking-[-.015em]" data-testid="scope-title">
            {scopeLabel(scope, cohorts)}
          </h1>
          <Stats items={stats(map, scope)} />
          {map.rows.length === 0 ? (
            <EmptyState
              where="карта когорты"
              title="Никого не нашлось"
              body={emptyBody(scope)}
              marks={[true, false, false]}
              alt={scope !== SCOPE_ALL ? { label: "посмотреть всех студентов", href: "/mentor/cohort/all" } : undefined}
            />
          ) : (
            <Matrix steps={map.steps} rows={map.rows} />
          )}
        </div>
        <aside className="flex flex-col gap-3.5 border-bd px-4 py-[18px] md:px-[18px] lg:border-l">
          <Attention map={map} />
          <Bottlenecks map={map} />
          <MentorLoadCard q={load} />
        </aside>
      </div>
    </Shell>
  );
}
